import { inject, provide } from 'vue';
import type { Component, InjectionKey } from 'vue';
import type { BlockNode } from './types';
import type { SharedBlockInfo, TypeInfo } from './useBuilderRegistry';

/**
 * The host contract: everything domain-specific the builder engine needs is
 * supplied by the embedding host through this object — block catalog, HTML
 * rendering, preview document, optional working copy and UI strings. The
 * engine itself stays free of routes, endpoints and lang namespaces; Content
 * is the first host (contentHost.ts), any future domain implements the same
 * interfaces against its own backend.
 *
 * The rendering interface is deliberately just "marker-annotated HTML":
 * hosts must return documents/fragments carrying the <!--pb:id:s/e-->
 * comment markers the bridge scans (see types.ts markerId()).
 */

/** Engine-facing translator; keys are builder-relative (e.g. 'tab_blocks'). */
export type BuilderTranslator = (key: string, params?: Record<string, unknown>) => string;

export interface BlockCatalog {
    loadTypes(): Promise<TypeInfo[]>;
    /** Absent = shared blocks disabled for this host. */
    loadSharedBlocks?(): Promise<SharedBlockInfo[]>;
}

export interface RenderService {
    renderLevel(level: BlockNode): Promise<{ html: string }>;
    renderTree(levels: BlockNode[]): Promise<{ html: string }>;
}

export interface PreviewSource {
    /** Iframe src; `reload` bumps on every forced reload (cache-bust). */
    previewUrl(reload: number): string;
    /** Canvas breakpoint widths; null = engine defaults (768/375). */
    breakpoints(): Promise<{ tablet: number; mobile: number } | null>;
}

/** Rejection marker for save/publish working-copy conflicts (HTTP 409). */
export class DraftConflictError extends Error {}

/** Non-conflict draft request failure; status 0 = network/no response. */
export class DraftRequestError extends Error {
    constructor(public readonly status: number) {
        super(`Draft request failed (${status})`);
    }
}

export interface DraftState {
    content: unknown;
    mode: 'edit' | 'readonly';
    lock: { user: string | null };
    updated_at: string | null;
    /** Whether the working copy differs from the published content. */
    changed?: boolean;
}

export interface DraftService {
    show(options?: { takeover?: boolean }): Promise<DraftState>;
    /** Rejects with DraftConflictError when the lock was lost. */
    save(content: BlockNode[]): Promise<{ saved_at: string }>;
    /** Fire-and-forget keepalive flush on tab hide/unload; owns its CSRF. */
    saveBeacon(content: BlockNode[]): void;
    heartbeat(): Promise<void>;
    /** Rejects with DraftConflictError when the live entity changed underneath. */
    publish(force: boolean): Promise<void>;
    discard(): Promise<void>;
}

/** The two editing surfaces the shell can mount. */
export type BuilderSurface = 'document' | 'canvas';

export interface DocumentMediaService {
    /** Uploads a pasted file; resolves with the host's raw media object. */
    upload(file: File): Promise<unknown>;
    /** Block node (sans id) representing the uploaded media in content. */
    imageBlock(media: unknown): Omit<BlockNode, 'id'>;
}

/**
 * Document-surface capability. Absent = the surface is unavailable for this
 * host (no toggle, canvas-only) — also how the server kill-switch manifests.
 */
export interface DocumentCapability {
    /** Surface opened when the user has no persisted preference. */
    default: BuilderSurface;
    /** Vue components resolving TypeInfo.document kind:'component' names. */
    components?: Record<string, Component>;
    /** Absent = clipboard image paste disabled. */
    media?: DocumentMediaService;
    /** Markdown-conversion targets: "# " → heading slug, "> " → quote slug.
     *  Absent quote = the editor's native blockquote input rule applies. */
    transforms?: { heading?: string; quote?: string };
}

/** One-click starter section offered on an empty canvas (ids omitted —
 *  insertion clones and regenerates them). */
export interface BlockTemplate {
    key: string;
    label: string;
    blocks: BlockNode[];
}

export interface BuilderHost {
    catalog: BlockCatalog;
    render: RenderService;
    preview: PreviewSource;
    /** Absent = no working copy (Canvas-lite: the host form owns the data). */
    draft?: DraftService;
    /** Absent = document surface unavailable (canvas-only host). */
    document?: DocumentCapability;
    /** Empty-canvas starter templates; unknown type slugs are filtered out. */
    templates?: BlockTemplate[];
    t: BuilderTranslator;
    /** localStorage namespace; defaults to 'pb2' (keeps existing user state). */
    storagePrefix?: string;
}

const BUILDER_HOST_KEY: InjectionKey<BuilderHost> = Symbol('builder-host');

/**
 * Provides the host to the builder component tree.
 *
 * @param host The host implementation supplied by the composition root.
 * @return void No return expected.
 */
export function provideBuilderHost(host: BuilderHost): void {
    provide(BUILDER_HOST_KEY, host);
}

export function useBuilderHost(): BuilderHost {
    const host = inject(BUILDER_HOST_KEY);

    if (!host) {
        throw new Error('useBuilderHost must be used inside a builder tree');
    }

    return host;
}

/** Builder-relative translator; the dominant host dependency of leaf components. */
export function useBuilderT(): BuilderTranslator {
    return useBuilderHost().t;
}
